import { useEffect, useRef, useState } from 'react';
import { ArrowLeft, Paperclip } from 'lucide-react';
import { onboardingAPI } from '../../services/api';
import AuthedImage, { AuthedLightbox } from '../AuthedImage';
import { InlineLoader } from '../loading';
import { downloadBlob } from '../../utils/download';

/**
 * Dashboard → Onboarding → Checklist & sign-off history → View.
 *
 * What one Filled cell says: the joiner's checklist or the buddy's (Sr iPro) sign-off
 * for one plan day, question by question, as it was filled. Rendered INSIDE the history
 * pop-up in place of its table (Back returns to it), never as a pop-up on a pop-up.
 *
 * Shapes mirror the API's onboarding-submission.service. Photos and files are API
 * paths behind the same Onboarding access as the history, so they are fetched with the
 * bearer (AuthedImage / AuthedLightbox), never opened by a public URL.
 */

export type OnboardingForm = 'checklist' | 'signoff';

export interface SubmissionFile { name: string; isImage: boolean; url: string }

export type SubmissionAnswer =
  | { kind: 'text'; text: string }
  | { kind: 'number'; value: number }
  | { kind: 'date'; date: string }
  | { kind: 'choice'; labels: string[] }
  | { kind: 'yesno'; yes: boolean }
  | { kind: 'rating'; value: number; max: number }
  | { kind: 'files'; files: SubmissionFile[] };

export interface SubmissionQuestion { id: string; text: string; type: string; answer: SubmissionAnswer | null }

export interface OnboardingSubmission {
  form: OnboardingForm;
  formTitle: string | null;
  employeeCode: string;
  date: string;
  filledBy: { role: 'joiner' | 'buddy'; code: string; name: string | null };
  submittedAt: string | null;
  questions: SubmissionQuestion[];
}

/** The history table's column names — also the form's name when it has no title. */
export const FORM_LABEL: Record<OnboardingForm, string> = {
  checklist: 'Onboarding checklist',
  signoff: 'Sr iPro sign-off',
};

const fmtDate = (iso: string): string => {
  const d = new Date(`${iso.slice(0, 10)}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

/** A submission time in IST, e.g. "10 Sept, 6:40 pm" (the history cell's own format). */
const fmtSubmitted = (ts: string | null): string => {
  if (!ts) return '';
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', hour: 'numeric', minute: '2-digit',
  });
};

const fmtNumber = (n: number): string => n.toLocaleString('en-IN', { maximumFractionDigits: 2 });

function Unanswered() {
  return (
    <span className="text-gray-400">
      <span aria-hidden="true">—</span>
      <span className="sr-only">Not answered</span>
    </span>
  );
}

function FileAnswer({ question, files, onOpenPhoto }: {
  question: string; files: SubmissionFile[]; onOpenPhoto: (p: { path: string; alt: string }) => void;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const [failed, setFailed] = useState<string | null>(null);

  const openFile = async (f: SubmissionFile) => {
    setBusy(f.url);
    setFailed(null);
    try {
      const res = await onboardingAPI.getAnswerFile(f.url);
      downloadBlob(f.name, res.data as Blob);
    } catch {
      setFailed(f.url);
    } finally {
      setBusy(null);
    }
  };

  const photoName = (i: number) => `Photo${files.length > 1 ? ` ${i + 1}` : ''}, ${question}`;
  return (
    <div className="flex flex-wrap items-start gap-2">
      {files.map((f, i) => (f.isImage ? (
        <button
          key={f.url}
          type="button"
          onClick={() => onOpenPhoto({ path: f.url, alt: photoName(i) })}
          aria-label={`Open ${photoName(i)}`}
          className="overflow-hidden rounded-lg border border-gray-200 transition hover:border-primary-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
        >
          <AuthedImage path={f.url} alt={photoName(i)} className="h-20 w-20 object-cover" />
        </button>
      ) : (
        <span key={f.url} className="inline-flex flex-col">
          <button
            type="button"
            onClick={() => { void openFile(f); }}
            disabled={busy === f.url}
            className="inline-flex max-w-[16rem] items-center gap-1.5 rounded-lg border border-gray-200 px-2.5 py-1.5 text-xs font-medium text-primary-700 transition hover:bg-primary-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 disabled:opacity-50"
          >
            <Paperclip className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            <span className="truncate">{f.name}</span>
          </button>
          {failed === f.url && <span className="mt-0.5 text-[11px] text-red-600">Couldn’t open this file.</span>}
        </span>
      )))}
    </div>
  );
}

function AnswerValue({ question, answer, onOpenPhoto }: {
  question: string; answer: SubmissionAnswer | null; onOpenPhoto: (p: { path: string; alt: string }) => void;
}) {
  if (!answer) return <Unanswered />;
  switch (answer.kind) {
    case 'text': return <p className="whitespace-pre-wrap break-words">{answer.text}</p>;
    case 'number': return <span>{fmtNumber(answer.value)}</span>;
    case 'date': return <span>{fmtDate(answer.date)}</span>;
    case 'choice': return <span>{answer.labels.join(', ')}</span>;
    case 'yesno': return <span>{answer.yes ? 'Yes' : 'No'}</span>;
    case 'rating': return <span className="font-semibold">{fmtNumber(answer.value)}/{answer.max}</span>;
    case 'files': return answer.files.length ? <FileAnswer question={question} files={answer.files} onOpenPhoto={onOpenPhoto} /> : <Unanswered />;
    default: return <Unanswered />;
  }
}

export default function OnboardingAnswersView({
  projectId, employeeCode, joinerName, form, date, dayLabel, onBack,
}: {
  projectId: number;
  employeeCode: string;
  /** The joiner's name from the worklist row — who filled a checklist. */
  joinerName: string;
  form: OnboardingForm;
  /** YYYY-MM-DD, the history row's date. */
  date: string;
  /** "Day N" as the history table numbers it. */
  dayLabel: number;
  onBack: () => void;
}) {
  const [data, setData] = useState<OnboardingSubmission | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [photo, setPhoto] = useState<{ path: string; alt: string } | null>(null);
  const backRef = useRef<HTMLButtonElement>(null);

  // Keyboard and screen-reader users land on Back, the way out of this view.
  useEffect(() => { backRef.current?.focus({ preventScroll: true }); }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    onboardingAPI.getJoinerAnswers({ projectId, employeeCode, form, date })
      .then((res) => { if (!cancelled) setData((res.data?.data ?? null) as OnboardingSubmission | null); })
      .catch(() => { if (!cancelled) { setData(null); setFailed(true); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [projectId, employeeCode, form, date, attempt]);

  const title = data?.formTitle || FORM_LABEL[form];
  const who = data
    ? data.filledBy.role === 'joiner'
      ? { name: joinerName || data.employeeCode, role: 'New joiner' }
      : { name: data.filledBy.name || data.filledBy.code || 'Buddy', role: 'Sr iPro (buddy)' }
    : null;
  const when = fmtSubmitted(data?.submittedAt ?? null);

  return (
    <section aria-label={`${FORM_LABEL[form]}, Day ${dayLabel}`} className="space-y-4">
      <div className="flex items-start gap-3">
        <button
          ref={backRef}
          type="button"
          onClick={onBack}
          className="mt-0.5 inline-flex shrink-0 items-center gap-1 rounded-md border border-gray-200 px-2 py-1 text-xs font-medium text-gray-700 transition hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
        >
          <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" />
          Back
        </button>
        <div className="min-w-0">
          <h4 className="text-sm font-semibold text-gray-900">{title}</h4>
          <div className="text-xs text-gray-500">Day {dayLabel} · {fmtDate(date)}</div>
          {who && (
            <div className="mt-0.5 text-xs text-gray-500">
              Filled by <span className="font-medium text-gray-800">{who.name}</span> · {who.role}{when ? ` · ${when}` : ''}
            </div>
          )}
        </div>
      </div>

      {loading ? (
        <div className="rounded-xl border border-gray-200 py-10 text-center text-sm text-gray-400">
          <InlineLoader label="Loading answers…" className="flex justify-center" />
        </div>
      ) : failed || !data ? (
        <div role="alert" className="flex flex-wrap items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <span>Couldn’t load these answers.</span>
          <button
            type="button"
            onClick={() => setAttempt((n) => n + 1)}
            className="rounded-md border border-red-300 bg-white px-2.5 py-1 text-xs font-semibold text-red-700 transition hover:bg-red-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500"
          >
            Try again
          </button>
        </div>
      ) : (
        <dl className="divide-y divide-gray-100 rounded-xl border border-gray-200">
          {data.questions.length === 0 ? (
            <div className="px-4 py-3 text-sm"><Unanswered /></div>
          ) : data.questions.map((q) => (
            <div key={q.id} className="grid grid-cols-1 gap-1 px-4 py-3 sm:grid-cols-5 sm:gap-4">
              <dt className="text-xs text-gray-600 sm:col-span-2">{q.text}</dt>
              <dd className="text-sm text-gray-900 sm:col-span-3">
                <AnswerValue question={q.text} answer={q.answer} onOpenPhoto={setPhoto} />
              </dd>
            </div>
          ))}
        </dl>
      )}

      {photo && <AuthedLightbox path={photo.path} alt={photo.alt} onClose={() => setPhoto(null)} />}
    </section>
  );
}
